import React from 'react';
import { dateRange, weekdays } from '../../utils/clientDates';
import { byPhase, ownerDisplay, phaseSummary, statusLabel, stepTone, type ProjectStep } from '../../utils/projectSteps';

/**
 * THE PROJECT TRACKER, as the client reads it.
 *
 * Redrawn 14 Oct 2026. Tom was shown three layouts and picked two halves:
 * *"Lets use B but with the pre-production, production, delivery stages of
 * C."* B was the ledger — four columns, one row per step, status in a column
 * of its own so it can be read straight down the page. C was the grouping.
 *
 * **Why a ledger and not the old spine.** The spine carried the status in a
 * dot, which meant the page had to be decoded before it could be read, and
 * at twenty-five steps the dots became wallpaper. Columns need no key. It is
 * also the shape of the Word tracker both companies were already keeping
 * between them, which is the strongest argument of the lot.
 *
 * **Each stage is its own small table.** Stage name and count, an ink rule,
 * the column heads, a hairline, then the rows. The heads were said once at
 * the very top in the first draft, which put two bands of small caps
 * directly above the first stage heading — Tom: *"sort the layout here —
 * looks a mess."* He was right twice over: the heads collided with the
 * heading, and WHO/STATUS sat immediately above the count line in the same
 * corner of the page, two unrelated small-caps lines stacked. Repeating four
 * words three times is a far smaller cost than that, and it re-anchors the
 * columns at each stage rather than asking the eye to carry them over
 * twenty-five rows.
 *
 * **The stage headings are the biggest type in the section** — 2.4rem, and
 * nothing else here is above 1.02rem. They shipped at 1.5rem and Tom, first
 * look: *"Lets make these subheadings bigger — i missed them on first look."*
 * A heading set only slightly above a row of ledger text has to compete with
 * twenty-five rows of it, and loses.
 *
 * **An undated step says so.** "To be scheduled", never a guess. The gaps are
 * the most useful thing on the page.
 *
 * **Three weights, five words.** `stepTone` decides the weight — attention
 * forward in ink, done quiet in mute, everything else in between — and the
 * status says the rest in words. The weight is for the glance down the page,
 * the words for the row you stop at.
 *
 * **Responsibility is a name where there is one.** "Marjan — Fadi /
 * Jehangir" rather than "Marjan", because a step addressed to everybody is
 * addressed to nobody.
 *
 * **It is honest in both directions, on purpose.** A late Portal step draws
 * exactly as loudly as a late client one. That is what makes it usable as
 * the record clause 3.9 of the Marjan agreement turns on — a dated, visible,
 * mutually-read account of what is outstanding and whose it is. A tracker
 * that only ever points at the client is a complaint, not a record.
 *
 * **Phase headings disappear on a short project.** `byPhase` drops empty
 * phases, and one surviving group draws no heading at all — grouping a
 * six-step job is furniture.
 *
 * **The columns start at `lg`, not `sm`.** Measured on the published page:
 * the client page caps its content at 1100px, so the fixed columns below
 * leave the step 560px at full width and 404px at 1024px. Below that there
 * is not enough room for four columns without crushing one, so the row
 * stacks — date, step, then who and status on one quiet line. Four columns
 * squeezed onto a phone is a spreadsheet, not a page. The head rules follow
 * the heads: below `lg` the stage's ink rule runs straight into the first
 * row, with no second hairline a few pixels under it. The who/status gap on
 * that stacked line is 24px rather than 12, checked on a phone — at 12 the
 * two read as one phrase ("PORTAL DONE").
 *
 * Read-only, matching the rest of the client page and Tom's *"I don't want
 * to train people to use this."*
 */

const GRID = 'lg:grid lg:grid-cols-[116px_minmax(0,1fr)_200px_140px] lg:gap-x-7';
export function ProjectTracker({
  steps,
  clientName
}: {
  steps: ProjectStep[];
  clientName: string;
}) {
  if (steps.length === 0) return null;
  const groups = byPhase(steps);
  const showPhases = groups.length > 1;
  return <div data-id="element-153" data-mp="5pyxevklu7ny">
      {groups.map((group, index) => <section key={group.phase} className={index === 0 ? '' : 'mt-20'} data-id="element-154" data-mp="9pg0xjgh0uqh" data-mpi={index}>
          {showPhases && <div className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2 border-b border-ink pb-4" data-id="element-155" data-mp="xf21aa8kxg02">
              <h3 className="m-0 font-display text-[1.9rem] font-light leading-none tracking-tight sm:text-[2.4rem]" data-id="element-156" data-mp="12dlnmbr34tsy">
                {group.label}
              </h3>
              <p className="label-sm m-0 text-mute" data-id="element-157" data-mp="sollijhadiaj">{phaseSummary(group.steps, clientName)}</p>
            </div>}

          {/* Column heads, under the stage rule where a table header belongs.
              Narrow screens drop them — the rows stack there, and a header
              row over a stack is a lie. */}
          <div className={`hidden pb-3 pt-4 ${GRID}`} aria-hidden data-id="element-158" data-mp="35us1dgscp090">
            <p className="label-sm m-0 text-mute" data-id="element-159" data-mp="30wgo0w2uk7m1">Date</p>
            <p className="label-sm m-0 text-mute" data-id="element-160" data-mp="30vxfg5z36vko">Step</p>
            <p className="label-sm m-0 text-mute" data-id="element-161" data-mp="3flwaw0z9bswg">Who</p>
            <p className="label-sm m-0 text-mute" data-id="element-162" data-mp="3fnfspw0kac0c">Status</p>
          </div>

          <ol className="m-0 list-none p-0" data-id="element-163" data-mp="3ams4tj7mqbac">
            {group.steps.map(step => {
          const tone = stepTone(step);
          const quiet = tone === 'done';
          const date = dateRange(step.start, step.end);
          const days = weekdays(step.start, step.end);
          return <li key={step.id} className={`border-b border-rule py-4 lg:items-baseline lg:py-[1.15rem] lg:first:border-t lg:first:border-rule ${GRID}`} data-id="element-164" data-mp="2ww6gvg3bjb5l">
                  <div data-id="element-165" data-mp="c6h5xe5qk9i6">
                    {date ? <>
                        <p className={`m-0 text-[1rem] font-light leading-tight ${quiet ? 'text-mute' : 'text-ink'}`} data-id="element-166" data-mp="759yxhbf3eck">
                          {date}
                        </p>
                        {days && <p className="label-sm mt-1 m-0 text-mute" data-id="element-167" data-mp="o7745798rakl">{days}</p>}
                      </> : <p className="label-sm m-0 text-mute" data-id="element-168" data-mp="t4z66u3lod2i">To be scheduled</p>}
                  </div>

                  <div className="mt-2 min-w-0 lg:mt-0" data-id="element-169" data-mp="3rph5mjz1w58n">
                    <p className={`m-0 text-[1.02rem] leading-snug ${quiet ? 'text-mute' : 'text-ink'}`} data-id="element-170" data-mp="gtfzrts1vva">
                      {step.title}
                    </p>
                    {step.note && <p className="mt-1.5 m-0 max-w-[52ch] text-[0.9rem] leading-relaxed text-graphite" data-id="element-171" data-mp="a8h5hn6rx2u4">
                        {step.note}
                      </p>}
                  </div>

                  {/* Two cells on a wide screen, one quiet line below that.
                      `lg:contents` drops the wrapper out of the layout so the
                      pair falls into its own columns. */}
                  <div className="mt-2.5 flex flex-wrap items-baseline gap-x-6 gap-y-1 lg:mt-0 lg:contents" data-id="element-172" data-mp="1m0gedsnp2rgw">
                    <p className={`label-sm m-0 ${quiet ? 'text-mute' : 'text-graphite'}`} data-id="element-173" data-mp="1h1lxiw9afczf">
                      {ownerDisplay(step, clientName)}
                    </p>
                    <p className={`label-sm m-0 ${tone === 'attention' ? 'font-medium text-ink' : tone === 'done' ? 'text-mute' : 'text-graphite'}`} data-id="element-174" data-mp="1c4dcmnwr8uuh">
                      {statusLabel(step.status, clientName)}
                    </p>
                  </div>
                </li>;
        })}
          </ol>
        </section>)}
    </div>;
}